import { apiFetch, readErrorMessage, readJson } from '../api/client';
import { mountApiBase } from '../fileBrowser/fileBrowserApi';

export interface StorageContentResponse {
  path: string;
  name: string;
  content: string;
  size: number;
  contentType: string;
  revision: string;
}

export interface StorageContentWriteRequest {
  path: string;
  content: string;
  contentType?: string;
  expectedRevision?: string;
}

export interface StorageContentSaveResult {
  revision: string;
  path: string;
}

export class ApiHttpError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiHttpError';
    this.status = status;
  }
}

export class ContentRevisionConflictError extends Error {
  readonly code = 'ContentRevisionConflict';
  readonly currentRevision: string | null;

  constructor(message: string, currentRevision: string | null) {
    super(message);
    this.name = 'ContentRevisionConflictError';
    this.currentRevision = currentRevision;
  }
}

export function contentApiUrl(mountId: string, path?: string): string {
  const base = `${mountApiBase(mountId)}/content`;
  if (!path) return base;
  return `${base}?path=${encodeURIComponent(path)}`;
}

export function buildSaveContentBody(
  path: string,
  content: string,
  options?: { contentType?: string; expectedRevision?: string }
): StorageContentWriteRequest {
  const body: StorageContentWriteRequest = { path, content };
  if (options?.contentType) body.contentType = options.contentType;
  if (options?.expectedRevision) body.expectedRevision = options.expectedRevision;
  return body;
}

export function copyPathForConflict(path: string): string {
  const normalized = path.replace(/\\/g, '/');
  const lastSlash = normalized.lastIndexOf('/');
  const dir = lastSlash >= 0 ? normalized.slice(0, lastSlash + 1) : '';
  const name = lastSlash >= 0 ? normalized.slice(lastSlash + 1) : normalized;
  const dot = name.lastIndexOf('.');
  if (dot > 0) {
    return `${dir}${name.slice(0, dot)}-copy${name.slice(dot)}`;
  }
  return `${dir}${name}-copy`;
}

export async function fetchContent(mountId: string, path: string): Promise<StorageContentResponse> {
  const res = await apiFetch(contentApiUrl(mountId, path));
  if (!res.ok) throw new ApiHttpError(await readErrorMessage(res), res.status);
  return readJson<StorageContentResponse>(res);
}

export async function saveContent(
  mountId: string,
  request: StorageContentWriteRequest
): Promise<StorageContentSaveResult> {
  const res = await apiFetch(contentApiUrl(mountId), {
    method: 'PUT',
    body: JSON.stringify(buildSaveContentBody(request.path, request.content, request))
  });

  if (res.status === 409) {
    const payload = await readJson<{ errorText?: string; currentRevision?: string }>(res);
    throw new ContentRevisionConflictError(
      payload.errorText ?? 'Content revision conflict.',
      typeof payload.currentRevision === 'string' ? payload.currentRevision : null
    );
  }

  if (!res.ok) throw new ApiHttpError(await readErrorMessage(res), res.status);

  const payload = await readJson<{
    item?: { revision?: string; path?: string };
    revision?: string;
    path?: string;
  }>(res);

  const revision = payload.item?.revision ?? payload.revision ?? '';
  const savedPath = payload.item?.path ?? payload.path ?? request.path;
  return { revision, path: savedPath };
}
